<template>
  <div>
    <NuxtLink
      :to="path"
      :title="title"
      class="promotion-card tw-group"
      @click="$emit('click')"
    >
      <QCard
        class="tw-relative tw-rounded-xl"
        :class="mobileHorizontal && 'tw-hidden lg:tw-block'"
      >
        <!-- Recommended Tag -->
        <BadgeRecommended v-if="recommended" />
        <!-- Image Section -->
        <div class="tw-relative">
          <MediaImageLazyLoad
            :src="image?.large || ''"
            :placeholder-src="image?.tiny || ''"
            :alt="title"
            :ratio="1 / 1"
            seo-optimize
          />
        </div>
        <!-- Background Overlay -->
        <div
          class="card-overlay normal-state"
          :class="mobileHorizontal && 'tw-hidden lg:tw-block'"
        />
        <div
          class="card-overlay hover-state"
          :class="mobileHorizontal && 'tw-hidden lg:tw-block'"
        />

        <!-- Read More Button -->
        <div
          class="read-more-button-container"
          :class="mobileHorizontal && 'tw-hidden lg:tw-flex'"
        >
          <div
            class="read-more-button-text"
            :class="{
              'tw-text-sm': titleTag === 'h4',
            }"
          >
            {{ $t("components.promotion.readMore") }}
          </div>
        </div>

        <!-- Title Section (overlay on image) -->
        <div
          class="absolute-bottom tw-mb-5 tw-mx-4 tw-text-center tw-overflow-hidden"
          :class="mobileHorizontal && 'tw-hidden lg:tw-block'"
        >
          <component
            :is="titleTag"
            class="tw-text-white tw-text-mb-title-4 lg:tw-text-dt-title-4 tw-w-full tw-px-6 tw-line-clamp-2 group-hover:-tw-translate-y-full tw-duration-700"
          >
            {{ title }}
          </component>
        </div>
      </QCard>

      <!-- Mobile horizontal: fixed image + flexible text side by side -->
      <div
        v-if="mobileHorizontal"
        class="tw-grid tw-grid-cols-8 tw-gap-4 lg:tw-hidden"
      >
        <div class="tw-col-span-3 tw-rounded-xl tw-overflow-hidden">
          <MediaImageLazyLoad
            :src="image?.medium || image?.large || ''"
            :placeholder-src="image?.tiny || ''"
            :alt="title"
            :ratio="1 / 1"
            seo-optimize
          />
        </div>
        <div class="tw-col-span-5">
          <component
            :is="titleTag"
            class="tw-text-black tw-line-clamp-2"
            :class="titleClass || 'tw-text-mb-title-5'"
          >
            {{ title }}
          </component>
          <DisplayPrice
            class="tw-mt-2"
            :options="options"
            :show-options-divider="false"
            :show-option-list="showOptionList"
            :min-price-option="minPriceOption"
            :max-price-option="maxPriceOption"
            :min-price-of-discounted-option="minPriceOfDiscountedOption"
            :max-price-of-discounted-option="maxPriceOfDiscountedOption"
            :price="price"
            :price-of-discounted="priceOfDiscounted"
            main-price-class="tw-text-mb-title-5"
            :single-product-price-container-class="singlePriceClass"
            discounted-class="tw-text-mb-body-tiny tw-line-through tw-text-gray"
          />
        </div>
      </div>
    </NuxtLink>

    <!-- Price below card (desktop when mobileHorizontal, always when not) -->
    <DisplayPrice
      v-if="!hideDesktopPrice"
      :class="mobileHorizontal ? 'tw-mt-3 tw-hidden lg:tw-block' : 'tw-mt-3'"
      :options="options"
      :show-options-divider="false"
      :show-option-list="showOptionList"
      :min-price-option="minPriceOption"
      :max-price-option="maxPriceOption"
      :min-price-of-discounted-option="minPriceOfDiscountedOption"
      :max-price-of-discounted-option="maxPriceOfDiscountedOption"
      :price="price"
      :price-of-discounted="priceOfDiscounted"
    />
  </div>
</template>

<script setup lang="ts">
defineProps({
  image: {
    type: Object as PropType<ImageMeta>,
    required: true,
  },
  path: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  options: {
    type: Array as PropType<ProductOption[]>,
    default: () => [],
  },
  showOptionList: {
    type: Boolean,
    default: true,
  },
  price: {
    type: Number,
    default: 0,
  },
  priceOfDiscounted: {
    type: Number,
    default: 0,
  },
  maxPriceOption: {
    type: Number,
    default: 0,
  },
  minPriceOption: {
    type: Number,
    default: 0,
  },
  minPriceOfDiscountedOption: {
    type: Number,
    default: 0,
  },
  maxPriceOfDiscountedOption: {
    type: Number,
    default: 0,
  },
  recommended: {
    type: Boolean,
    default: false,
  },
  mobileHorizontal: {
    type: Boolean,
    default: false,
  },
  hideDesktopPrice: {
    type: Boolean,
    default: false,
  },
  titleTag: {
    type: String,
    default: "h3",
  },
  titleClass: {
    type: String,
    default: "",
  },
  singlePriceClass: {
    type: String,
    default: "",
  },
});

// Emits
defineEmits(["click"]);
</script>

<style lang="scss" scoped>
.promotion-card {
  @apply tw-block tw-rounded-xl
    tw-bg-white tw-overflow-hidden
    tw-duration-200 hover:-tw-translate-y-2;
}

.card-overlay {
  @apply tw-absolute tw-inset-0;
}

.normal-state {
  @apply tw-bg-gradient-to-t tw-from-primary tw-to-transparent tw-via-transparent tw-via-40%
    tw-opacity-100 group-hover:tw-opacity-0 tw-transition-opacity tw-duration-300;
}

.hover-state {
  @apply tw-bg-primary tw-opacity-0 group-hover:tw-opacity-75 tw-transition-opacity tw-duration-300;
}

.read-more-button-container {
  @apply tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center
  tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-500;

  .read-more-button-text {
    @apply tw-p-4 tw-bg-white tw-rounded-md tw-shadow-lg tw-translate-y-16
    group-hover:tw-translate-y-0 tw-duration-500;
  }
}
</style>
